import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import type { AuthChangeEvent, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabaseClient';
import { DESIGN_PREVIEW, PREVIEW_SIGNED_OUT, previewUser } from '../lib/designPreview';
import { apiFetch } from '../lib/apiClient';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string) => Promise<any>;
  signIn: (email: string, password: string) => Promise<any>;
  signOut: () => Promise<any>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Events that should trigger Neon profile init (outside the auth listener). */
export function shouldInitializeProfile(event: AuthChangeEvent): boolean {
  return event === 'SIGNED_IN' || event === 'INITIAL_SESSION' || event === 'TOKEN_REFRESHED';
}

/**
 * Initialize Neon profile using a token already present on the auth event.
 * Must not call supabase.auth.* — callers pass the access token in.
 */
export async function ensureNeonProfile(accessToken: string): Promise<void> {
  try {
    if (!accessToken) {
      console.error('Failed to initialize Neon profile: missing access token');
      return;
    }

    await apiFetch('/api/init-user', {
      method: 'POST',
      token: accessToken,
      body: {},
    });
  } catch (error) {
    console.error('Error initializing Neon profile:', error);
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(
    DESIGN_PREVIEW && !PREVIEW_SIGNED_OUT ? previewUser : null
  );
  const [loading, setLoading] = useState(!DESIGN_PREVIEW);
  const lastProfileUserId = useRef<string | null>(null);

  useEffect(() => {
    if (DESIGN_PREVIEW) return;

    let cancelled = false;

    const queueProfileInit = (userId: string, accessToken: string) => {
      if (lastProfileUserId.current === userId) return;
      lastProfileUserId.current = userId;
      void ensureNeonProfile(accessToken);
    };

    (async () => {
      try {
        const sessionLookup = supabase.auth.getSession();
        const timeout = new Promise<never>((_, reject) => {
          window.setTimeout(() => reject(new Error('session lookup timed out')), 8000);
        });
        const {
          data: { session },
        } = await Promise.race([sessionLookup, timeout]);
        if (cancelled) return;
        const nextUser = session?.user ?? null;
        setUser(nextUser);
        if (nextUser && session?.access_token) {
          queueProfileInit(nextUser.id, session.access_token);
        }
      } catch (error) {
        console.error('Initial auth session lookup failed:', error);
        if (!cancelled) {
          setUser(null);
          lastProfileUserId.current = null;
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Listener must stay synchronous and must not call supabase.auth methods
    // (nested getSession/getUser deadlocks the auth lock).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const nextUser = session?.user ?? null;
      setUser(nextUser);
      setLoading(false);

      if (!nextUser) {
        lastProfileUserId.current = null;
        return;
      }

      if (shouldInitializeProfile(event) && session?.access_token) {
        queueProfileInit(nextUser.id, session.access_token);
      }
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string) => {
    if (DESIGN_PREVIEW) return { user: previewUser, session: null };
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    if (DESIGN_PREVIEW) return { user: previewUser, session: null };
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    if (DESIGN_PREVIEW) return;
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const value = {
    user,
    loading,
    signUp,
    signIn,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
